'use client';

import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export interface PopoverProps {
  /** Trigger element. Popover anchors to its bounding box. */
  trigger: React.ReactElement;
  /** Popover body content. Can be interactive (buttons, links, forms). */
  children: React.ReactNode;
  /** Preferred side. Falls back to opposite side if it would clip. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment along the side. */
  align?: 'start' | 'center' | 'end';
  /** Classes applied to the popover panel. */
  className?: string;
  /** Controlled open state. Omit for uncontrolled. */
  open?: boolean;
  /** Called when open state changes (controlled or uncontrolled). */
  onOpenChange?: (open: boolean) => void;
  /** Focus the first interactive child on open (for linked information panels). */
  focusInteractiveOnOpen?: boolean;
  /** Keep closed content in server HTML, hidden until the panel opens. */
  renderClosedContent?: boolean;
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Close when clicking outside. Default true. */
  closeOnOutsideClick?: boolean;
  /**
   * ARIA role applied to the panel. Default 'dialog' for interactive popovers
   * with form controls or actions. Use 'region' for informational popovers
   * (e.g. FieldHint `?` icons, AboutThisScreen info popover) so screen readers
   * don't falsely announce "dialog" and avoid unnecessary focus-trap behaviour.
   */
  role?: 'dialog' | 'region' | 'tooltip' | 'menu';
  /** Accessible label when role='region'. Defaults to "More information". */
  ariaLabel?: string;
  /**
   * z-index for the portalled panel. Default 55. Raise this above a higher
   * stacking context when the popover is opened from inside one — e.g. the
   * mobile nav drawer renders at z-70, so its submenu popovers must pass a
   * value > 70 or they render *behind* the opaque drawer (invisible on mobile).
   */
  zIndex?: number;
}

/**
 * Popover — click-to-open floating panel. Good for `?` icon hints with
 * interactive content (links, buttons). For pure hover-text use Tooltip.
 *
 * The trigger must accept `onClick` and `ref` — typically a Button.
 */
export function Popover({
  trigger,
  children,
  side = 'bottom',
  align = 'center',
  className,
  open: controlledOpen,
  onOpenChange,
  focusInteractiveOnOpen = false,
  renderClosedContent = false,
  closeOnEscape = true,
  closeOnOutsideClick = true,
  role = 'dialog',
  ariaLabel,
  zIndex = 55,
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    if (renderClosedContent) setHasMounted(true);
  }, [renderClosedContent]);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const popoverId = useId();

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const panelW = panelRef.current?.offsetWidth ?? 280;
    const panelH = panelRef.current?.offsetHeight ?? 120;

    let top = 0;
    let left = 0;

    switch (side) {
      case 'bottom':
        top = rect.bottom + margin;
        left =
          align === 'start'
            ? rect.left
            : align === 'end'
              ? rect.right - panelW
              : rect.left + rect.width / 2 - panelW / 2;
        if (top + panelH > window.innerHeight) {
          top = rect.top - panelH - margin; // flip
        }
        break;
      case 'top':
        top = rect.top - panelH - margin;
        left =
          align === 'start'
            ? rect.left
            : align === 'end'
              ? rect.right - panelW
              : rect.left + rect.width / 2 - panelW / 2;
        if (top < 0) top = rect.bottom + margin;
        break;
      case 'right':
        left = rect.right + margin;
        top =
          align === 'start'
            ? rect.top
            : align === 'end'
              ? rect.bottom - panelH
              : rect.top + rect.height / 2 - panelH / 2;
        break;
      case 'left':
        left = rect.left - panelW - margin;
        top =
          align === 'start'
            ? rect.top
            : align === 'end'
              ? rect.bottom - panelH
              : rect.top + rect.height / 2 - panelH / 2;
        break;
    }

    // Clamp to viewport
    const clampedPanelW = Math.min(panelW, window.innerWidth - margin * 2);
    const clampedPanelH = Math.min(panelH, window.innerHeight - margin * 2);
    left = Math.max(margin, Math.min(left, window.innerWidth - clampedPanelW - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - clampedPanelH - margin));
    setPosition({ top, left });
  }, [side, align]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    computePosition();
    // Recompute on resize / scroll
    const handler = () => computePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent | PointerEvent) => {
      if (!closeOnOutsideClick) return;
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      // For linked information, a blank-area touch must not blur the restored
      // trigger through its compatibility mouse event. Focusable outside targets
      // retain their default focus (including another information button).
      if (
        focusInteractiveOnOpen &&
        target instanceof Element &&
        !target.closest('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
      )
        e.preventDefault();
      triggerRef.current?.focus();
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('pointerdown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('pointerdown', handleClick);
    };
  }, [isOpen, closeOnEscape, closeOnOutsideClick, focusInteractiveOnOpen, setOpen]);

  // Portalled links are outside the trigger's tab sequence. Enter the panel
  // once it is visible; restore focus if a controlled close removes it.
  const focusedOnOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      focusedOnOpen.current = false;
      return;
    }
    if (!focusInteractiveOnOpen || !position || focusedOnOpen.current) return;
    const panel = panelRef.current;
    const interactive = panel?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
    );
    interactive?.focus();
    focusedOnOpen.current = true;
  }, [isOpen, position, focusInteractiveOnOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    const triggerNode = triggerRef.current;
    return () => {
      if (panel?.contains(document.activeElement) || document.activeElement === document.body) {
        triggerNode?.focus();
      }
    };
  }, [isOpen]);

  const handleEscape = (event: React.KeyboardEvent) => {
    if (isOpen && closeOnEscape && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const triggerElement = React.cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const triggerAny = trigger as React.ReactElement & { ref?: React.Ref<HTMLElement> };
      const origRef = triggerAny.ref;
      if (typeof origRef === 'function') origRef(node);
      else if (origRef && typeof origRef === 'object') {
        (origRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onClick: (e: React.MouseEvent) => {
      setOpen(!isOpen);
      (trigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      handleEscape(event);
      (trigger.props as { onKeyDown?: (event: React.KeyboardEvent) => void }).onKeyDown?.(event);
    },
    'aria-expanded': isOpen,
    'aria-haspopup': role === 'dialog' ? 'dialog' : role === 'menu' ? 'menu' : undefined,
    'aria-controls': isOpen ? popoverId : undefined,
  } as Record<string, unknown>);

  const panel =
    isOpen && typeof window !== 'undefined'
      ? createPortal(
          <div
            id={popoverId}
            ref={panelRef}
            data-popover-layer=""
            onKeyDown={handleEscape}
            role={role}
            aria-label={role !== 'dialog' ? (ariaLabel ?? 'More information') : undefined}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              zIndex,
              visibility: position ? 'visible' : 'hidden',
            }}
            className={cn(
              'min-w-0 max-w-[calc(100vw-1rem)] overflow-auto break-words font-body text-body-sm text-text-body rounded-overlay border border-border bg-surface-sheet p-4 shadow-popover sm:min-w-[240px] sm:max-w-sm',
              'motion-reduce:animate-none animate-in fade-in-0 zoom-in-95 duration-150',
              className,
            )}
          >
            {children}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {triggerElement}
      {renderClosedContent && !isOpen && !hasMounted ? <div hidden>{children}</div> : null}
      {panel}
    </>
  );
}
